/**
 * SB-7554 — logistics tickets raised by Statiq Tools Production Planning.
 *
 * The backend stamps such a ticket once, at raise, and returns the stamp on the ticket as
 * `production_movement` ("dispatch" | "inward"; null on every other ticket). Every rule here keys off
 * that field only, so an ordinary ticket renders exactly as before. The backend refuses the actions
 * this hides (409/403); hiding them only spares the user a refusal.
 */

export type ProductionMovement = "dispatch" | "inward"

type WithMovement = { production_movement?: string | null } | null | undefined
type PpTicket = {
    production_movement?: string | null
    job_work_set_by?: JobWorkSetBy
    production_plan_id?: number | null
    job_work_id?: string | null
    status_id?: number | null
    status?: string | null
} | null | undefined

/** The Job Work card's fetch button (JobWorkZohoCard) — one string, so the mismatch message names it as labelled. */
export const JOB_WORK_FETCH_LABEL = "Fetch DC details from Zoho"
export const PRODUCTION_MOVEMENT_CAPTION = "Raised by Statiq Tools Production Planning"
/** Who wrote the job-work number, as ticket details reports it (`job_work_set_by`); null when unknown. */
export type JobWorkSetBy = { by?: "production_planning" | "user" | null; user_name?: string | null } | null | undefined
export const JOB_WORK_SET_BY_PP = "Set by Production Planning"
export const JOB_WORK_SET_UNKNOWN = "Job work on this Production Planning ticket"
/** Said wherever this user cannot set the number themselves (U2: ops add it only while Open and empty). */
export const JOB_WORK_ASK_SUPER_ADMIN = "Ask a Super Admin to correct it."
/** Read-only hints only: an empty number is read-only once the ticket has left Open. */
export const JOB_WORK_NOT_SET_HINT = `Not set yet. ${JOB_WORK_ASK_SUPER_ADMIN}`
/** The status modal while the field is offered right under it (Open, number empty). */
export const JOB_WORK_REQUIRED_MESSAGE = "Add the job-work number here to move this ticket to Dispatch Initiated."
/** The status modal when this user may not set it. */
export const JOB_WORK_REQUIRED_READ_ONLY_MESSAGE = `This dispatch needs its job-work number before it can move on. ${JOB_WORK_ASK_SUPER_ADMIN}`

const OPEN_STATUS_ID = 40
const DISPATCHED_STATUS_ID = 42

/** PP-00014, as Production Planning shows its plans. */
export const planDisplayId = (planId?: number | null): string | null =>
    planId ? `PP-${String(planId).padStart(5, "0")}` : null

const statusIdOf = (ticket: PpTicket): number | null => {
    if (typeof ticket?.status_id === "number") return ticket.status_id
    const s = String(ticket?.status || "").toLowerCase()
    return s === "open" ? OPEN_STATUS_ID : s === "dispatched" ? DISPATCHED_STATUS_ID : s === "delivered" ? 43 : null
}

const isOpen = (ticket: PpTicket) => statusIdOf(ticket) === OPEN_STATUS_ID
export const TEMPLATE_LOCKED_MESSAGE = "Production Planning tickets keep their template"

// Full wording: the list marker's tooltip, the tile's tooltip.
const LABELS: Record<ProductionMovement, string> = {
    dispatch: "Dispatch to manufacturer",
    inward: "Inward to warehouse",
}
// The ticket tile reads "Production · To manufacturer": short enough for one line in a 150px tile at 1440 (item 17,
// measured in the rig), the tile's tooltip carries the full wording.
const TILE_LABELS: Record<ProductionMovement, string> = {
    dispatch: "To manufacturer",
    inward: "To warehouse",
}
export const PRODUCTION_TILE_MIN_WIDTH = "150px"

/** Buttons a Production Planning ticket does not offer (the backend refuses them with 409). */
const HIDDEN_BUTTON_KEYS = new Set(["link_solo_ticket"])

export const productionMovementOf = (ticket: WithMovement): ProductionMovement | null => {
    const m = ticket?.production_movement
    return m === "dispatch" || m === "inward" ? m : null
}

export const isProductionMovementTicket = (ticket: WithMovement): boolean => productionMovementOf(ticket) !== null

export const productionMovementLabel = (ticket: WithMovement): string | null => {
    const m = productionMovementOf(ticket)
    return m ? LABELS[m] : null
}

export const productionMovementTileLabel = (ticket: WithMovement): string | null => {
    const m = productionMovementOf(ticket)
    return m ? TILE_LABELS[m] : null
}

/** Job work on a Production Planning ticket (U2, 18 Oct — the backend enforces the same rule):
 *  - ops may ADD it while the ticket is Open and the number is empty;
 *  - changing or clearing a set number is the planner's (Statiq Tools) or a super admin's — the backend logs
 *    pp_job_work_corrected with the signed-in user.
 * Ordinary tickets keep today's rule (`allowedOtherwise`). */
export const canEditJobWork = (ticket: PpTicket, allowedOtherwise: boolean, isSuperAdmin = false): boolean => {
    if (!isProductionMovementTicket(ticket)) return allowedOtherwise
    if (isSuperAdmin) return true
    return isOpen(ticket) && !String(ticket?.job_work_id || "").trim()
}

/** Who set it, from what the backend recorded — never assumed: since U2 ops may set it too, so "Set by Production
 * Planning" only when the backend says so, the user's name when a signed-in user did, and a neutral line otherwise. */
export const jobWorkSetByText = (setBy: JobWorkSetBy): string => {
    if (setBy?.by === "production_planning") return JOB_WORK_SET_BY_PP
    if (setBy?.by === "user") return setBy.user_name ? `Set by ${setBy.user_name}` : "Set by a Cristalina user"
    return JOB_WORK_SET_UNKNOWN
}

/** What a read-only job-work field says on a Production Planning ticket. The fetch tip only while Dispatched is still
 * ahead (item 17); after Dispatched / Delivered it only says who set it. */
export const jobWorkHint = (ticket: PpTicket): string => {
    if (!String(ticket?.job_work_id || "").trim()) return JOB_WORK_NOT_SET_HINT
    const setBy = jobWorkSetByText(ticket?.job_work_set_by)
    const sid = statusIdOf(ticket)
    const base =
        sid !== null && sid >= DISPATCHED_STATUS_ID
            ? setBy
            : `${setBy} — at Dispatched, "${JOB_WORK_FETCH_LABEL}" pulls the delivery challan and e-way bill`
    return `${base}. ${JOB_WORK_ASK_SUPER_ADMIN}`
}

/** The Super Admin correction (18 Oct): the plan in Statiq Tools keeps the old number until the planner changes it. */
export const jobWorkCorrectionWarning = (oldValue: string): string =>
    `Statiq Tools Production Planning still has ${oldValue} on the plan. Correcting it here does not change the ` +
    `plan: ask the planner to update it there too.`

/** A Production Planning DISPATCH ticket that has no job-work number cannot leave Open (the backend refuses 41/42/43
 * with "Add the job-work number first"); the status modal says so up front and offers the field. */
export const needsJobWorkBeforeLeavingOpen = (ticket: PpTicket): boolean =>
    productionMovementOf(ticket) === "dispatch" && !String(ticket?.job_work_id || "").trim()

/** Item 12: a delivery-challan lookup on a Production Planning ticket whose challan belongs to a DIFFERENT job work.
 * Returned so the modal can block Save and name both numbers; null when there is nothing to block. */
export type DcJobWorkMismatch = { dcNumber: string; dcJobWork: string; ticketJobWork: string | null }
export const dcJobWorkMismatch = (
    ticket: PpTicket,
    dcNumber: string,
    dcJobWork: string | null | undefined
): DcJobWorkMismatch | null => {
    const linked = String(dcJobWork || "").trim()
    if (!isProductionMovementTicket(ticket) || !linked) return null
    const own = String(ticket?.job_work_id || "").trim()
    return linked === own ? null : { dcNumber, dcJobWork: linked, ticketJobWork: own || null }
}
export const dcMismatchMessage = (m: DcJobWorkMismatch): string =>
    `DC ${m.dcNumber} belongs to job work ${m.dcJobWork}, but this Production Planning ticket's job work is ` +
    `${m.ticketJobWork || "not set"}. Save is blocked: ` +
    (m.ticketJobWork
        ? `press "${JOB_WORK_FETCH_LABEL}" in the Job Work card to use ${m.ticketJobWork}'s own challan, or type this ` +
          `ticket's own DC number. `
        : `type this ticket's own DC number. `) +
    `If the job work itself is wrong, ask a Super Admin to correct it.`

/** Item 14: what the drop confirmation says on a Production Planning ticket — its charger lines go back to the plan. */
export const dropReleaseMessage = (ticket: PpTicket & { production_drop_releases?: number | null }): string | null => {
    if (!isProductionMovementTicket(ticket)) return null
    const n = Number(ticket?.production_drop_releases ?? 0)
    const plan = planDisplayId(ticket?.production_plan_id) || "its plan"
    if (n <= 0)
        return `This Production Planning ticket has no charger lines, so dropping it releases nothing; ${plan} can raise it again.`
    return `Dropping this Production Planning ticket releases its ${n} charger${n === 1 ? "" : "s"} back to ${plan}, so the planner can raise ${n === 1 ? "it" : "them"} on a new ticket.`
}

/** The list's Deal Type cell: a Production Planning ticket is an internal material movement, not a CPO deal (its
 * template's deal_type is cpo only for the finance trigger), so it reads "Production Planning" (round 3, item 3d). */
export const PRODUCTION_PLANNING_DEAL_LABEL = "Production Planning"
export const ticketDealTypeLabel = (ticket: WithMovement & { deal_type?: string | null }): string | null | undefined =>
    isProductionMovementTicket(ticket) ? PRODUCTION_PLANNING_DEAL_LABEL : ticket?.deal_type

/** A delivery-challan lookup may re-link the ticket's job work only on an ordinary ticket. */
export const mayRelinkJobWorkFromDc = (ticket: WithMovement): boolean => !isProductionMovementTicket(ticket)

/** The status dropdown without the actions a Production Planning ticket refuses. Accepts the list or
 * the `{ data: [...] }` envelope the endpoint returns, and returns the same shape. */
export function withoutRefusedButtons<T extends { button_status_key?: string }>(buttons: T[], ticket: WithMovement): T[]
export function withoutRefusedButtons<T>(buttons: T, ticket: WithMovement): T
export function withoutRefusedButtons(buttons: any, ticket: WithMovement): any {
    if (!isProductionMovementTicket(ticket) || !buttons) return buttons
    const keep = (list: any[]) => list.filter(b => !HIDDEN_BUTTON_KEYS.has(b?.button_status_key))
    if (Array.isArray(buttons)) return keep(buttons)
    if (Array.isArray(buttons?.data)) return { ...buttons, data: keep(buttons.data) }
    return buttons
}

/** A saved project whose template is a Production Planning one (its ui_rules carry production_movement) keeps
 * that template: the cards are disabled. A new project (no id yet) is never locked. */
export const isTemplateLocked = (projectId: unknown, uiRules: { production_movement?: string | null } | undefined) =>
    Boolean(projectId) && Boolean(uiRules?.production_movement)

/** The message to show for a refused template change: the backend's own (409), else a fallback. */
export const templateChangeErrorMessage = (error: any): string =>
    error?.status === 409
        ? error?.data?.message || TEMPLATE_LOCKED_MESSAGE
        : error?.data?.message || "Could not change the template"

/** The partial-stock headline on a delivered inward ticket (round 3, item 3c): how many of the delivery's chargers are
 * now in stock, how many are not, and that each one below says why and what to do. */
export const partialStockHeadline = (stocked: number, total: number, notStocked: number): string =>
    `Partly stocked: ${stocked} of ${total} charger${total === 1 ? "" : "s"} on this delivery ` +
    `${stocked === 1 ? "is" : "are"} now In-stock Warehouse. ${notStocked} ` +
    `${notStocked === 1 ? "was" : "were"} not stocked and keep${notStocked === 1 ? "s its" : " their"} old status — ` +
    `each one below says why and what to do next.`
